import React from "react";
import { Container, Alert, CircularProgress } from "@mui/material";
import FileList from "../FileList";
import { SelectPicker } from "rsuite";
import { ICompany } from "modules/companies/types";
import { useCompanies } from "shared/hooks/useCompanies";
import { folderNav } from "modules/files/services";
import { IFolderNav } from "modules/files/types";
import { getById } from "modules/companies/services";
import { useIntl, defineMessages } from "react-intl";

const messages = defineMessages({
  selectCompany: { id: "folder.navigation.select.company" },
  noCompanySelected: { id: "folder.navigation.no.company.selected" },
  noSupport: { id: "folder.navigation.no.support" },
});

interface IKeyValue {
  label: string;
  value: number;
}

interface IFilter {
  companyId: number;
  path: string;
  action: "prev" | "next";
}

const FolderNavigation: React.FC = () => {
  const { formatMessage } = useIntl();
  const { companies } = useCompanies(true);
  const [localCompanies, setLocalCompanies] = React.useState<IKeyValue[]>([]);
  const [files, setFiles] = React.useState<IFolderNav[]>([]);
  const [filters, setFilters] = React.useState<IFilter>({} as IFilter);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [enabled, setEnabled] = React.useState<boolean | null>(null);
  const [pathList, setPathList] = React.useState<string[]>([]);

  const handleCompanies = React.useCallback(() => {
    const cm: IKeyValue[] = [];
    companies.forEach((company: ICompany) =>
      cm.push({ label: company.fantasy_name, value: company.id || 0 })
    );
    setLocalCompanies(cm);
  }, [companies]);

  const handleFiles = React.useCallback(async () => {
    if (!filters.companyId) return false;
    setLoading(true);

    const { data: dataCompany } = await getById(filters.companyId);

    let companyEnabled = dataCompany.folder_navigation ? true : false;

    if (!companyEnabled) {
      setLoading(false);
      setEnabled(false);
    } else {
      setEnabled(true);
    }

    const { status, data } = await folderNav(filters.companyId, filters.path);
    if ([200, 201].includes(status)) {
      setFiles(data);
      setLoading(false);
    }
  }, [filters]);

  const handlePath = React.useCallback(
    (path: string, action: "prev" | "next") => {
      let pathListCopy: string[] = pathList;

      if (action === "next") {
        pathListCopy.push(path);
      } else {
        if (pathListCopy.length > 1) {
          pathListCopy = pathListCopy.slice(0, -1);
        }
      }

      path = pathListCopy[pathListCopy.length - 1];

      setPathList(pathListCopy);
      setFilters({
        ...filters,
        action,
        path,
      });
    },
    [filters, pathList]
  );

  React.useEffect(() => {
    handleCompanies();
  }, [handleCompanies]);

  React.useEffect(() => {
    handleFiles();
  }, [handleFiles]);

  return (
    <Container maxWidth="xl">
      <div style={{ width: "25%", marginRight: 10, marginBottom: 20 }}>
        <label>
          <b>{formatMessage(messages.selectCompany)}</b>
        </label>
        <SelectPicker
          data={localCompanies}
          placeholder={formatMessage(messages.selectCompany)}
          style={{ width: "100%" }}
          onChange={(companyId: number) => {
            setFilters({ ...filters, companyId, action: "next" });
          }}
        />
      </div>
      <>
        {loading ? (
          <CircularProgress size={30} />
        ) : (
          <>
            {enabled === true && (
              <FileList
                rows={files}
                path={filters.path}
                onClick={(path, action) => {
                  handlePath(path, action);
                }}
              />
            )}
            {enabled === null && (
              <Alert severity="info">
                {formatMessage(messages.noCompanySelected)}
              </Alert>
            )}
            {enabled === false && (
              <Alert severity="error">
                {formatMessage(messages.noSupport)}
              </Alert>
            )}
          </>
        )}
      </>
    </Container>
  );
};

export { FolderNavigation };
